import {
  faCancel,
  faClose,
  faCreditCard,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemberInvoice, useMutationMemberInvoice } from "@gymflow/api";
import { downloadPdfBlob } from "@gymflow/helpers";
import { UserMemberBean } from "@gymflow/types";
import { PencilIcon } from "@heroicons/react/20/solid";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { ModalContext } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { useStaffGetInvoiceReceipt } from "libs/api/src/lib/hooks/staff/useStaffGetInvoiceReceipt";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { ApiResolverContext } from "../../../../providers/ApiResolverProvider/ApiResolverContext";
import {
  Button,
  DownloadIcon,
  MenuPopover,
  MenuPopoverOptionType,
  RefreshIcon,
} from "../../../atoms";
import { CollectPastDueInvoiceSidebarProviderContext } from "../../../organisms";
import { ConfirmModal } from "../../../templates";
import { INVOICE_STATUS } from "../constants";
import { UserMemberAdjustInvoiceModal } from "../UserMemberAdjustInvoiceModal";

type PaymentActionsMenuPopoverProps = {
  invoiceNumber?: string;
  memberId?: string;
  member?: UserMemberBean;
  closeSidebar: () => void;
};

export const PaymentsActionsMenuPopover = ({
  invoiceNumber,
  memberId,
  member,
  closeSidebar,
}: PaymentActionsMenuPopoverProps) => {
  const { t } = useTranslation();
  const { stackModal, hide } = useContext(ModalContext);
  const { openCollectPastDueInvoiceSidebar } = useContext(
    CollectPastDueInvoiceSidebarProviderContext,
  );
  const showScaModal = useScaModal({
    asMember: false,
  });
  const { api } = useGymflowModels();
  const { mutateAsync: downloadInvoice } = useMutationMemberInvoice({
    api,
  });
  const { data: invoice } = useStaffGetInvoiceReceipt({
    api,
    invoiceNumber,
  });
  const { clubId } = useContext(ApiResolverContext);
  const {
    refundInvoiceMutation,
    writeOffInvoiceMutation,
    cancelAuthorizationMutation,
  } = useMemberInvoice({
    api,
    memberId,
    clubId,
    invoiceNumber,
  });

  const actions: MenuPopoverOptionType[] = [];

  if (!invoice?.status || !memberId) return null;

  const { status } = invoice;

  if (status) {
    actions.push({
      id: "download",
      icon: <DownloadIcon pathClassName="stroke-gray-500" className="size-4" />,
      label: t("components.paymentsSidebarProvider.invoiceActions.download"),
      classNameLabel: "text-gray-950",
      onClick: async () => {
        const { number } = invoice;
        const { data: html } = await downloadInvoice({
          invoiceNumber: number,
        });
        downloadPdfBlob(html, `Gymflow-${number}.pdf`);
      },
    });
  }

  if (status === INVOICE_STATUS.PAID && invoice.totalAmount > 0) {
    actions.push({
      id: "refund",
      icon: <RefreshIcon className="size-4" />,
      label: t("components.paymentsSidebarProvider.invoiceActions.refund"),
      classNameLabel: "text-gray-950",
      onClick: () => {
        stackModal(
          <ConfirmModal
            type="warning"
            title={t(
              "components.paymentsSidebarProvider.invoiceActions.refundInvoice",
            )}
            onCancel={() => hide()}
            onConfirm={async () => {
              await refundInvoiceMutation.mutateAsync({
                invoiceNumber: invoice.number,
              });
              hide();
            }}
          >
            {t(
              "components.paymentsSidebarProvider.refundInvoiceConfirmationText",
            )}
          </ConfirmModal>,
        );
      },
    });
  }

  if (status === INVOICE_STATUS.AWAITING_AUTHORIZATION) {
    actions.push(
      {
        id: "cancelAuth",
        icon: <FontAwesomeIcon icon={faClose} className="size-4" />,
        label: t(
          "components.paymentsSidebarProvider.invoiceActions.cancelAuth",
        ),
        classNameLabel: "text-gray-950",
        onClick: () => {
          cancelAuthorizationMutation.mutateAsync({
            invoiceNumber: invoice.number,
          });
        },
      },
      {
        id: "authorize",
        icon: <FontAwesomeIcon icon={faCreditCard} className="size-4" />,
        label: t("components.paymentsSidebarProvider.invoiceActions.authorize"),
        onClick: () => {
          showScaModal({
            invoiceNumber: invoice.number,
            userMemberId: memberId,
          });
        },
      },
    );
  }

  if (status === INVOICE_STATUS.PAST_DUE) {
    actions.push(
      {
        id: "collect",
        icon: <FontAwesomeIcon icon={faCreditCard} className="size-4" />,
        label: t("components.paymentsSidebarProvider.invoiceActions.collect"),
        classNameLabel: "text-gray-950",
        onClick: () => {
          closeSidebar();
          openCollectPastDueInvoiceSidebar({
            invoiceNumber: invoice.number,
            userMemberId: memberId,
          });
        },
      },
      {
        id: "writeOff",
        icon: <FontAwesomeIcon icon={faCancel} className="size-4" />,
        label: t("components.paymentsSidebarProvider.invoiceActions.writeOff"),
        classNameLabel: "text-gray-950",
        onClick: () => {
          writeOffInvoiceMutation.mutateAsync({
            invoiceNumber: invoice.number,
          });
        },
      },
    );
  }

  if (
    status === INVOICE_STATUS.PAST_DUE ||
    status === INVOICE_STATUS.SCHEDULED
  ) {
    actions.push({
      id: "adjust",
      icon: <PencilIcon className="size-4" />,
      label: t("components.paymentsSidebarProvider.invoiceActions.adjust"),
      classNameLabel: "text-gray-950",
      onClick: () => {
        if (!member || !invoice) return;
        stackModal(
          <UserMemberAdjustInvoiceModal
            invoice={invoice}
            userMember={member}
          />,
        );
      },
    });
  }

  if (actions.length === 0) return null;

  return (
    <MenuPopover
      classNameContent="w-48 flex flex-col gap-1 p-2"
      triggerButton={
        <Button className="size-9 p-0">
          <FontAwesomeIcon
            icon={faEllipsisV}
            className="size-4 text-gray-950"
          />
        </Button>
      }
      side="bottom"
      options={actions}
    />
  );
};
